import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import LandingStyleHeader from '../components/LandingStyleHeader';
import { auth } from '../firebaseConfig';

export default function DashboardScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [greeting, setGreeting] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [placeholderText, setPlaceholderText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);

  const placeholderExamples = [
    'create proposal for web redesign...',
    'organize: call client, review mockups, finalize design...',
    'case study for e-commerce platform...',
    'add template for mobile app proposals...',
    'generate proposal about building a fitness app...',
  ];

  useEffect(() => {
    document.title = 'Flomodo - Dashboard';
    
    // Set greeting based on time of day
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  // Typing animation effect for placeholder
  useEffect(() => {
    if (isFocused) return; // Don't animate when focused

    let currentExampleIndex = 0;
    let currentCharIndex = 0;
    let typingTimeout;
    let pauseTimeout;
    const typingSpeed = 80;
    const pauseDuration = 2000;
    const deletingSpeed = 40;

    const typeNextChar = () => {
      const currentExample = placeholderExamples[currentExampleIndex];
      
      if (isTyping) {
        // Typing forward
        if (currentCharIndex < currentExample.length) {
          setPlaceholderText(currentExample.substring(0, currentCharIndex + 1));
          currentCharIndex++;
          typingTimeout = setTimeout(typeNextChar, typingSpeed);
        } else {
          // Finished typing, pause then delete
          pauseTimeout = setTimeout(() => {
            setIsTyping(false);
            typeNextChar();
          }, pauseDuration);
        }
      } else {
        // Deleting backward
        if (currentCharIndex > 0) {
          currentCharIndex--;
          setPlaceholderText(currentExample.substring(0, currentCharIndex));
          typingTimeout = setTimeout(typeNextChar, deletingSpeed);
        } else {
          // Finished deleting, move to next example
          currentExampleIndex = (currentExampleIndex + 1) % placeholderExamples.length;
          setIsTyping(true);
          setTimeout(typeNextChar, 300);
        }
      }
    };

    typeNextChar();

    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(pauseTimeout);
    };
  }, [isFocused]);

  const userName = auth.currentUser?.email?.split('@')[0] || 'there';

  const quickLinks = [
    {
      id: 'proposal',
      title: 'Create Proposal',
      description: 'Generate professional proposals with AI',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      action: () => navigation.navigate('wizard'),
    },
    {
      id: 'tasks',
      title: 'Task Manager',
      description: 'Organize tasks from conversations',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      action: () => navigation.navigate('tasks'),
    },
    {
      id: 'casestudy',
      title: 'Case Study Creator',
      description: 'Generate compelling project case studies',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      action: () => navigation.navigate('casestudy'),
    },
    {
      id: 'templates',
      title: 'Templates & Examples',
      description: 'Manage your templates and project examples',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      action: () => navigation.navigate('settings'),
    },
  ];

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    
    const query = searchQuery.toLowerCase();
    const content = searchQuery.trim();
    
    // Intelligent routing with context preservation
    if (query.includes('proposal') || query.includes('generate proposal') || query.includes('create proposal')) {
      // Extract potential project description after the command
      const descMatch = content.match(/(?:proposal|create|generate)\s+(?:for|about)?\s*(.+)/i);
      const projectDesc = descMatch ? descMatch[1] : '';
      
      // Navigate with pre-filled content
      if (projectDesc) {
        // Store in sessionStorage to pass to wizard
        sessionStorage.setItem('pendingProjectDescription', projectDesc);
      }
      navigation.navigate('wizard');
    } else if (query.includes('task') || query.includes('todo') || query.includes('organize')) {
      // Extract tasks or conversation
      const taskMatch = content.match(/(?:task|todo|organize)\s*:?\s*(.+)/i);
      const taskContent = taskMatch ? taskMatch[1] : '';
      
      if (taskContent) {
        sessionStorage.setItem('pendingTaskInput', taskContent);
      }
      navigation.navigate('tasks');
    } else if (query.includes('case study') || query.includes('casestudy')) {
      // Extract project details
      const caseMatch = content.match(/(?:case study|casestudy)\s+(?:for|about)?\s*(.+)/i);
      const caseContent = caseMatch ? caseMatch[1] : '';
      
      if (caseContent) {
        sessionStorage.setItem('pendingCaseStudyName', caseContent);
      }
      navigation.navigate('casestudy');
    } else if (query.includes('template') || query.includes('example') || query.includes('add template') || query.includes('add example')) {
      navigation.navigate('settings');
    } else if (query.includes('setting') || query.includes('preference') || query.includes('theme') || query.includes('profile') || query.includes('account')) {
      navigation.navigate('profile');
    } else {
      // Smart default: if it looks like content, go to proposal wizard
      if (content.length > 20) {
        sessionStorage.setItem('pendingProjectDescription', content);
        navigation.navigate('wizard');
      } else {
        // Show a helpful message or default to proposal
        navigation.navigate('wizard');
      }
    }
    
    // Clear search after navigation
    setSearchQuery('');
  };

  return (
    <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="dashboard" />
      
      <ScrollView 
        style={[styles.content, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]} 
        contentContainerStyle={styles.contentContainer}
        onScroll={(e) => {
          // Manually dispatch scroll event to window so header can detect it
          const scrollY = e.nativeEvent.contentOffset.y;
          window.lastKnownScrollY = scrollY;
          window.dispatchEvent(new Event('scroll'));
        }}
        scrollEventThrottle={16}
      >
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <Text style={[styles.greeting, { color: colors.text }]}>{greeting}, {userName}!</Text>
          <Text style={[styles.question, { color: colors.textSecondary }]}>What would you like to do today?</Text>
          
          {/* AI Search Bar */}
          <View style={styles.searchContainer}>
            <View 
              style={[
                styles.searchBar, 
                { ...glass, backgroundColor: colors.card },
                isSearchHovered && { borderColor: colors.accent, borderWidth: 2 },
                isFocused && { borderRadius: 20, borderColor: colors.accent, borderWidth: 2, boxShadow: `0 0 0 3px ${colors.accent}33` }
              ]}
              onMouseEnter={() => setIsSearchHovered(true)}
              onMouseLeave={() => setIsSearchHovered(false)}
            >
              <TextInput
                style={[styles.searchInput, { color: colors.text }]}
                placeholder={isFocused ? "Type a command or paste content..." : placeholderText}
                placeholderTextColor={colors.textTertiary}
                value={searchQuery}
                onChangeText={setSearchQuery}
                onSubmitEditing={handleSearch}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  setPlaceholderText('');
                }}
                returnKeyType="go"
              />
              <TouchableOpacity 
                style={[styles.searchButton, { backgroundColor: colors.accent }]}
                onPress={handleSearch}
              >
                <Text style={styles.searchButtonText}>Go</Text>
              </TouchableOpacity>
            </View>
            <Text style={[styles.searchHint, { color: colors.textSecondary }]}>
              Try: "generate proposal for web redesign" • "organize: call client, review mockups" • "case study e-commerce site"
            </Text>
          </View>
        </View>

        {/* Quick Links Grid */}
        <View style={styles.quickLinksSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Quick Actions</Text>
          
          <View style={styles.quickLinksGrid}>
            {quickLinks.map((link) => (
              <TouchableOpacity
                key={link.id}
                style={[
                  styles.quickLinkCard,
                  { 
                    ...glass, 
                    backgroundColor: colors.card, 
                    borderColor: colors.border,
                    transform: hoveredCard === link.id ? [{ scale: 1.02 }] : [{ scale: 1 }],
                    boxShadow: hoveredCard === link.id ? '0 12px 48px rgba(0, 0, 0, 0.12)' : '0 8px 32px rgba(0, 0, 0, 0.08)',
                  }
                ]}
                onPress={link.action}
                onMouseEnter={() => setHoveredCard(link.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <View style={[styles.quickLinkIcon, { backgroundImage: link.gradient }]} />
                <View style={styles.quickLinkContent}>
                  <Text style={[styles.quickLinkTitle, { color: colors.text }]}>{link.title}</Text>
                  <Text style={[styles.quickLinkDescription, { color: colors.textSecondary }]}>{link.description}</Text>
                </View>
                <Text style={[
                  styles.quickLinkArrow, 
                  { 
                    color: colors.accent,
                    transform: hoveredCard === link.id ? [{ translateX: 4 }] : [{ translateX: 0 }]
                  }
                ]}>→</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingBottom: 60,
  },
  
  // Hero Section
  heroSection: {
    padding: 60,
    paddingTop: 140,
    alignItems: 'center',
  },
  greeting: {
    fontSize: 48,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 12,
    textAlign: 'center',
  },
  question: {
    fontSize: 24,
    color: '#64748b',
    marginBottom: 48,
    textAlign: 'center',
  },
  
  // Search Bar
  searchContainer: {
    width: '100%',
    maxWidth: 800,
    alignItems: 'center',
  },
  searchBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    padding: 8,
    paddingLeft: 16,
    paddingRight: 8,
    gap: 8,
    borderWidth: 2,
    borderColor: '#e2e8f0',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.3s ease',
    overflow: 'hidden', // Prevents content from overflowing
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1e293b',
    padding: 12,
    outlineStyle: 'none',
    minWidth: 0, // Prevents flex item from overflowing
  },
  searchButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0, // Prevents button from shrinking
    minWidth: 60, // Ensures button has minimum width
  },
  searchButtonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  searchHint: {
    marginTop: 12,
    fontSize: 14,
    color: '#94a3b8',
    textAlign: 'center',
  },
  
  // Quick Links
  quickLinksSection: {
    padding: 60,
    paddingTop: 80,
  },
  sectionTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 32,
  },
  quickLinksGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  quickLinkCard: {
    flex: 1,
    minWidth: 280,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 24,
    borderRadius: 20,
    borderWidth: 1,
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  quickLinkIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickLinkEmoji: {
    fontSize: 32,
  },
  quickLinkContent: {
    flex: 1,
  },
  quickLinkTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1a1a2e',
    marginBottom: 4,
  },
  quickLinkDescription: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
  },
  quickLinkArrow: {
    fontSize: 24,
    color: '#cbd5e0',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
});

